import React, {useEffect, useRef, useState} from 'react';
import {Box, CircularProgress, Paper, TableContainer} from '@mui/material';
import {TaxHeader, DepreciationScheduleTable, CommercialBuildScheduleTable} from '@/pages/components/tax-computation/sub';
import {useCompanyAnnualStore, useTaxStore} from '@/store/audit';
import type {DepreciationScheduleRow, BfPools} from '@/pages/components/tax-computation/sub/DepreciationScheduleTable';
import type {CommercialBuildRow} from '@/pages/components/tax-computation/sub/CommercialBuildScheduleTable';
import {computeCommercialAllowanceFields} from '@/pages/components/tax-computation/sub/CommercialBuildScheduleTable';
import type {AuditTaxComPropertyBO} from '@/services/auditService/interface/tax-com';
import {applySavedPropertiesToCommercialSection, mergePropertyStampEdits, poolsEqual} from '@/pages/components/tax-computation/taxComPayload';

type CommercialBuildSection = {
    priorLocationsProperties: CommercialBuildRow[];
    yearLocationsProperties: CommercialBuildRow[];
    valuePriorYear: number;
    valueThisYear: number;
    reducingBf: number;
    wdvBf?: number;
    addDuringTheYear?: number;
    annualAllowance?: number;
    totalAllowance?: number;
    salesProceed?: number;
    balancingCharge?: number;
    subtotalAfterAllowance?: number;
    subtotalAfterSales?: number;
    reducingValueCf?: number;
    fullyUsedInPriorYears?: number;
    priorTitle?: string;
    yearTitle?: string;
};

interface TaxSubP1Props {
    annualId: number;
    /** Prefer parent companyId (TaxDetailsTab) — local annual-cache lookup can miss and skip table hydrate */
    companyId?: number;
    onPropertiesInit?: (properties: AuditTaxComPropertyBO[]) => void;
    oneReducingBf?: number;
    oneReducingBfChange?: (bf: number) => void;
    wdvBf?: number;
    wdvBfChange?: (bf: number) => void;
    wdvBfPools?: BfPools | null;
    wdvBfPoolsChange?: (next: BfPools) => void;
    /** MULTI: lock W.D.V. b/f (read-only + skip syncing from balancingList). */
    wdvBfEditable?: boolean;
    baPools?: BfPools | null;
    onBaPoolsChange?: (next: BfPools) => void;
    /** Backend balancingList Less: Sales proceed (display / WDV c/f only; not user-edited). */
    salesProceedPools?: BfPools | null;
    /** Push backend-hydrated sales proceed pools to parent (TaxDetailsTab). */
    onSalesProceedPoolsChange?: (next: BfPools) => void;
    commercialFullyUsedPriorYears?: number;
    commercialFullyUsedPriorYearsChange?: (v: number) => void;
    leaseholdFullyUsedPriorYears?: number;
    leaseholdFullyUsedPriorYearsChange?: (v: number) => void;
    commercialPriorLabel?: string | null;
    commercialPriorValue?: number | null;
    onCommercialPriorOverrideChange?: (label: string | null, value: number | null) => void;
    leaseholdPriorLabel?: string | null;
    leaseholdPriorValue?: number | null;
    onLeaseholdPriorOverrideChange?: (label: string | null, value: number | null) => void;
    skipParentPoolSyncRef?: React.MutableRefObject<boolean>;
    preferredPropertiesAfterSaveRef?: React.MutableRefObject<AuditTaxComPropertyBO[] | null>;
}

const mergeSectionStampEdits = (
    built: CommercialBuildSection,
    local: CommercialBuildSection | null,
): CommercialBuildSection => {
    if (!local) return built;

    const mergeRows = (apiList: CommercialBuildRow[], localList: CommercialBuildRow[]) =>
        apiList.map((apiRow, index) => {
            const localRow =
                localList.find((row) => row.ppeId != null && row.ppeId === apiRow.ppeId) ?? localList[index];
            if (!localRow) return apiRow;

            const apiStamp = Number(apiRow.stampDuty) || 0;
            const apiFee = Number(apiRow.commisionLegalFees) || 0;
            const localStamp = Number(localRow.stampDuty) || 0;
            const localFee = Number(localRow.commisionLegalFees) || 0;

            if (localStamp === apiStamp && localFee === apiFee) return apiRow;
            return {...apiRow, stampDuty: localStamp, commisionLegalFees: localFee};
        });

    return {
        ...built,
        priorLocationsProperties: mergeRows(
            built.priorLocationsProperties || [],
            local.priorLocationsProperties || [],
        ),
        yearLocationsProperties: mergeRows(
            built.yearLocationsProperties || [],
            local.yearLocationsProperties || [],
        ),
    };
};

export function TaxSubP1({
                             annualId,
                             companyId: companyIdProp,
                             onPropertiesInit,
                             oneReducingBf,
                             oneReducingBfChange,
                             wdvBf, wdvBfChange,
                             wdvBfPools, wdvBfPoolsChange,
                             wdvBfEditable = true,
                             baPools, onBaPoolsChange,
                             salesProceedPools, onSalesProceedPoolsChange,
                             commercialFullyUsedPriorYears,
                             commercialFullyUsedPriorYearsChange,
                             leaseholdFullyUsedPriorYears,
                             leaseholdFullyUsedPriorYearsChange,
                             commercialPriorLabel,
                             commercialPriorValue,
                             onCommercialPriorOverrideChange,
                             leaseholdPriorLabel,
                             leaseholdPriorValue,
                             onLeaseholdPriorOverrideChange,
                             skipParentPoolSyncRef,
                             preferredPropertiesAfterSaveRef,
                         }: TaxSubP1Props) {
    const getTaxComputationInfo = useCompanyAnnualStore((state) => state.getTaxComputationInfo);
    const getCompanyDetailsInfo = useCompanyAnnualStore((state) => state.getCompanyDetailsInfo);
    const periodFrom = useCompanyAnnualStore((state) => state.byAnnualId[annualId]?.periodFrom ?? null);

    const initialize = useTaxStore((state) => state.initialize);
    const p11Data = useTaxStore((state) => state.p11Data);

    const [loading, setLoading] = useState(true);
    const [taxInfo, setTaxInfo] = useState<{
        companyName: string;
        yearOfAssessment: string | null;
        presentationCurrency: string | null;
        taxFileNo: string | null;
    } | null>(null);
    const [depreciationRows, setDepreciationRows] = useState<DepreciationScheduleRow[]>([]);
    const [commercialRowsA, setCommercialRowsA] = useState<CommercialBuildSection | null>(null);
    const [commercialRowsB, setCommercialRowsB] = useState<CommercialBuildSection | null>(null);
    const [commercialPropsA, setCommercialPropsA] = useState<AuditTaxComPropertyBO[]>([]);
    const [commercialPropsB, setCommercialPropsB] = useState<AuditTaxComPropertyBO[]>([]);
    const [industrialProps, setIndustrialProps] = useState<AuditTaxComPropertyBO[]>([]);
    const commercialRowsARef = useRef<CommercialBuildSection | null>(null);
    const commercialRowsBRef = useRef<CommercialBuildSection | null>(null);

    useEffect(() => {
        commercialRowsARef.current = commercialRowsA;
    }, [commercialRowsA]);

    useEffect(() => {
        commercialRowsBRef.current = commercialRowsB;
    }, [commercialRowsB]);

    useEffect(() => {
        onPropertiesInit?.([
            ...(commercialPropsA || []),
            ...(commercialPropsB || []),
            ...(industrialProps || []),
        ]);
    }, [commercialPropsA, commercialPropsB, industrialProps, onPropertiesInit]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const info = await getTaxComputationInfo(annualId);
                setTaxInfo(info);

                const propCid = Number(companyIdProp) || 0;
                let nextCompanyId = propCid;
                if (!(nextCompanyId > 0)) {
                    const companyDetails = await getCompanyDetailsInfo(annualId);
                    nextCompanyId = Number(companyDetails?.companyId || 0) || 0;
                }

                if (nextCompanyId) {
                    // 初始化 taxStore（父级已 init 时会按 yoa 跳过，不重复打接口）
                    await initialize(nextCompanyId, annualId);
                } else {
                    setDepreciationRows([]);
                    setCommercialRowsA(null);
                    setCommercialRowsB(null);
                    setCommercialPropsA([]);
                    setCommercialPropsB([]);
                    setIndustrialProps([]);
                }
            } catch (error) {
                setDepreciationRows([]);
                setCommercialRowsA(null);
                setCommercialRowsB(null);
                setCommercialPropsA([]);
                setCommercialPropsB([]);
                setIndustrialProps([]);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [annualId, companyIdProp, getTaxComputationInfo, getCompanyDetailsInfo, initialize]);

    useEffect(() => {
        // Hydrate tables from store p11Data even when local companyId resolve lags behind parent.
        // Previously `if (!companyId) return` left Depreciation/Commercial tables unmounted.
        if (!p11Data) {
            setDepreciationRows([]);
            setCommercialRowsA(null);
            setCommercialRowsB(null);
            setCommercialPropsA([]);
            setCommercialPropsB([]);
            setIndustrialProps([]);
            return;
        }


        const buildProperties = (commercial: any): AuditTaxComPropertyBO[] => {
            const prior = (commercial?.priorLocationsProperties || []).map((r: any) => {
                const out: AuditTaxComPropertyBO = {
                    annualId,
                    ppeId: r?.ppeId ?? undefined,
                    stampDuty: Number(r?.stampDuty) || 0,
                    clFee: Number(r?.commissionLegalFee) || 0,
                    propertyType: 0,
                };
                if (r?.propertyId !== null && r?.propertyId !== undefined) {
                    out.id = r.propertyId;
                }
                return out;
            });

            const year = (commercial?.yearLocationsProperties || []).map((r: any) => {
                const out: AuditTaxComPropertyBO = {
                    annualId,
                    ppeId: r?.ppeId ?? undefined,
                    stampDuty: Number(r?.stampDuty) || 0,
                    clFee: Number(r?.commissionLegalFee) || 0,
                    propertyType: 1,
                };
                if (r?.propertyId !== null && r?.propertyId !== undefined) {
                    out.id = r.propertyId;
                }
                return out;
            });

            return [...prior, ...year];
        };

        const savedSnapshot = skipParentPoolSyncRef?.current
            ? preferredPropertiesAfterSaveRef?.current
            : null;

        setCommercialPropsA((prev) => mergePropertyStampEdits(
            buildProperties(p11Data?.commercialBuilding),
            savedSnapshot ?? prev,
        ));
        setCommercialPropsB((prev) => mergePropertyStampEdits(
            buildProperties(p11Data?.thisYearCommercialBuilding),
            savedSnapshot ?? prev,
        ));
        setIndustrialProps(buildProperties(p11Data?.industrialBuilding));

        const rows: DepreciationScheduleRow[] = (p11Data?.balancingList || []).map((r) => ({
            item: r?.rowName || '',
            tenPool: Number(r?.tenPool) || 0,
            twentyPool: Number(r?.twentyPool) || 0,
            thirtyPool: Number(r?.thirtyPool) || 0,
            hpThirtyPool: Number(r?.hpThirtyPool) || 0,
            balancingCharge: Number((r as any)?.balancingCharge) || 0,
            balancingAllowance: Number((r as any)?.balancingAllowance) || 0,
            allowance: Number((r as any)?.allowance) || 0,
        }));
        setDepreciationRows(rows);

        const buildSection = (commercial: any, opts: { leasehold: boolean; bf: number; priorValueOverride?: number | null }): CommercialBuildSection => {
            const priorLocationsProperties: CommercialBuildRow[] = (commercial?.priorLocationsProperties || []).map((r: any) => ({
                date: r?.date || null,
                item: r?.equipmentName || '',
                costPerBook: Number(r?.costPerBook) || 0,
                stampDuty: Number(r?.stampDuty) || 0,
                commisionLegalFees: Number(r?.commissionLegalFee) || 0,
                ppeId: r?.ppeId != null ? Number(r.ppeId) : undefined,
                propertyId: r?.propertyId != null ? Number(r.propertyId) : undefined,
            }));
            const yearLocationsProperties: CommercialBuildRow[] = (commercial?.yearLocationsProperties || []).map((r: any) => ({
                date: r?.date || null,
                item: r?.equipmentName || '',
                costPerBook: Number(r?.costPerBook) || 0,
                stampDuty: Number(r?.stampDuty) || 0,
                commisionLegalFees: Number(r?.commissionLegalFee) || 0,
                ppeId: r?.ppeId != null ? Number(r.ppeId) : undefined,
                propertyId: r?.propertyId != null ? Number(r.propertyId) : undefined,
            }));

            const fullyUsedInPriorYears = Number(commercial?.fullyUsedInPriorYears) || 0;
            const scheduleBf = opts.leasehold
                ? (Number(commercial?.wdvBf ?? commercial?.reducingValueBf) || opts.bf)
                : (Number(commercial?.reducingValueBf) || opts.bf);
            const allowanceFields = computeCommercialAllowanceFields(
                {priorLocationsProperties, yearLocationsProperties, fullyUsedInPriorYears},
                scheduleBf,
                opts.leasehold,
                opts.priorValueOverride,
            );

            return {
                priorLocationsProperties,
                yearLocationsProperties,
                valuePriorYear: Number(commercial?.priorCostProperties) || 0,
                valueThisYear: Number(commercial?.yearCastProperties) || 0,
                reducingBf: scheduleBf,
                wdvBf: Number(commercial?.wdvBf ?? commercial?.reducingValueBf) || 0,
                fullyUsedInPriorYears,
                addDuringTheYear: commercial?.addDuringTheYear != null
                    ? Number(commercial.addDuringTheYear) : allowanceFields.addDuringTheYear,
                annualAllowance: commercial?.annualAllowance != null
                    ? Number(commercial.annualAllowance) : allowanceFields.annualAllowance,
                totalAllowance: commercial?.totalAllowance != null
                    ? Number(commercial.totalAllowance) : allowanceFields.totalAllowance,
                salesProceed: Number(commercial?.salesProceed) || 0,
                balancingCharge: Number(commercial?.balancingCharge) || 0,
                subtotalAfterAllowance: commercial?.subtotalAfterAllowance != null
                    ? Number(commercial.subtotalAfterAllowance) : undefined,
                subtotalAfterSales: commercial?.subtotalAfterSales != null
                    ? Number(commercial.subtotalAfterSales) : undefined,
                reducingValueCf: commercial?.reducingValueCf != null
                    ? Number(commercial.reducingValueCf) : undefined,
            };
        };

        const sectionABuilt = buildSection(p11Data?.commercialBuilding, {
            leasehold: false,
            // 可编辑 Reducing value b/f 以父级为准，避免 refresh 脏缓存把本地 BF 盖回旧值
            bf: Number(oneReducingBf) || 0,
            priorValueOverride: commercialPriorValue,
        });
        const sectionBBuilt = buildSection(p11Data?.thisYearCommercialBuilding, {
            leasehold: true,
            bf: Number(wdvBf) || 0,
            priorValueOverride: leaseholdPriorValue,
        });

        const sectionA = savedSnapshot?.length
            ? applySavedPropertiesToCommercialSection(sectionABuilt, savedSnapshot)
            : mergeSectionStampEdits(sectionABuilt, commercialRowsARef.current);
        const sectionB = savedSnapshot?.length
            ? applySavedPropertiesToCommercialSection(sectionBBuilt, savedSnapshot)
            : mergeSectionStampEdits(sectionBBuilt, commercialRowsBRef.current);

        setCommercialRowsA(sectionA);
        setCommercialRowsB(sectionB);
    }, [annualId, oneReducingBf, p11Data, wdvBf, commercialPriorValue, leaseholdPriorValue, skipParentPoolSyncRef, preferredPropertiesAfterSaveRef]);

    // Sync editable pool fields from backend only when P1.1 data refreshes — not when user edits parent pool state.
    useEffect(() => {
        if (!p11Data || skipParentPoolSyncRef?.current) {
            return;
        }

        const rows: DepreciationScheduleRow[] = (p11Data?.balancingList || []).map((r) => ({
            item: r?.rowName || '',
            tenPool: Number(r?.tenPool) || 0,
            twentyPool: Number(r?.twentyPool) || 0,
            thirtyPool: Number(r?.thirtyPool) || 0,
            hpThirtyPool: Number(r?.hpThirtyPool) || 0,
        }));

        // MULTI later YOA: parent carries prior W.D.V. c/f — do not overwrite from this YOA's balancingList
        if (wdvBfEditable) {
            const wdvRow = rows.find((r) => (r?.item || '').trim() === 'W.D.V. b/f') || null;
            if (wdvRow) {
                const nextPools = {
                    tenPool: Number(wdvRow.tenPool) || 0,
                    twentyPool: Number(wdvRow.twentyPool) || 0,
                    thirtyPool: Number(wdvRow.thirtyPool) || 0,
                    hpThirtyPool: Number(wdvRow.hpThirtyPool) || 0,
                };
                if (!poolsEqual(nextPools, wdvBfPools)) {
                    wdvBfPoolsChange?.(nextPools);
                }
            }
        }

        const baRow = rows.find((r) => (r?.item || '').trim() === 'Balancing allowance') || null;
        if (baRow) {
            const nextPools = {
                tenPool: Number(baRow.tenPool) || 0,
                twentyPool: Number(baRow.twentyPool) || 0,
                thirtyPool: Number(baRow.thirtyPool) || 0,
                hpThirtyPool: Number(baRow.hpThirtyPool) || 0,
            };
            if (!poolsEqual(nextPools, baPools)) {
                onBaPoolsChange?.(nextPools);
            }
        }

        const salesProceedRow = rows.find((r) => (r?.item || '').trim() === 'Less: Sales proceed') || null;
        if (salesProceedRow) {
            const nextPools = {
                tenPool: Number(salesProceedRow.tenPool) || 0,
                twentyPool: Number(salesProceedRow.twentyPool) || 0,
                thirtyPool: Number(salesProceedRow.thirtyPool) || 0,
                hpThirtyPool: Number(salesProceedRow.hpThirtyPool) || 0,
            };
            if (!poolsEqual(nextPools, salesProceedPools)) {
                onSalesProceedPoolsChange?.(nextPools);
            }
        }
    }, [p11Data, skipParentPoolSyncRef, wdvBfEditable, wdvBfPoolsChange, onBaPoolsChange, onSalesProceedPoolsChange]);

    useEffect(() => {
        if (skipParentPoolSyncRef?.current) return;
        if (commercialRowsA?.fullyUsedInPriorYears !== undefined && commercialRowsA.fullyUsedInPriorYears !== commercialFullyUsedPriorYears) {
            commercialFullyUsedPriorYearsChange?.(commercialRowsA.fullyUsedInPriorYears);
        }
    }, [commercialRowsA?.fullyUsedInPriorYears, commercialFullyUsedPriorYears, commercialFullyUsedPriorYearsChange, skipParentPoolSyncRef]);

    useEffect(() => {
        if (skipParentPoolSyncRef?.current) return;
        if (commercialRowsB?.fullyUsedInPriorYears !== undefined && commercialRowsB.fullyUsedInPriorYears !== leaseholdFullyUsedPriorYears) {
            leaseholdFullyUsedPriorYearsChange?.(commercialRowsB.fullyUsedInPriorYears);
        }
    }, [commercialRowsB?.fullyUsedInPriorYears, leaseholdFullyUsedPriorYears, leaseholdFullyUsedPriorYearsChange, skipParentPoolSyncRef]);

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                <CircularProgress/>
            </Box>
        );
    }

    if (!taxInfo) {
        return <Box sx={{p: 2}}>Failed to load data</Box>;
    }

    const applySectionEditsToProps = (section: CommercialBuildSection | null, base: AuditTaxComPropertyBO[]) => {
        if (!section) {
            return base;
        }
        const prior = section.priorLocationsProperties || [];
        const year = section.yearLocationsProperties || [];

        const next = (base || []).map((p) => ({...p}));
        let idx = 0;

        for (let i = 0; i < prior.length; i += 1) {
            if (next[idx]) {
                next[idx].stampDuty = Number(prior[i]?.stampDuty) || 0;
                next[idx].clFee = Number(prior[i]?.commisionLegalFees) || 0;
            }
            idx += 1;
        }
        for (let i = 0; i < year.length; i += 1) {
            if (next[idx]) {
                next[idx].stampDuty = Number(year[i]?.stampDuty) || 0;
                next[idx].clFee = Number(year[i]?.commisionLegalFees) || 0;
            }
            idx += 1;
        }

        return next;
    };

    const handleCommercialRowsAChange = (nextSection: any) => {
        setCommercialRowsA(nextSection);
        setCommercialPropsA((prev) => applySectionEditsToProps(nextSection, prev));
    };

    const handleCommercialRowsBChange = (nextSection: any) => {
        setCommercialRowsB(nextSection);
        setCommercialPropsB((prev) => applySectionEditsToProps(nextSection, prev));
    };

    return (
        <Box sx={{p: 3}}>
            <TaxHeader
                companyName={taxInfo.companyName}
                fileNo={taxInfo.taxFileNo || ''}
                yearOfAssessment={taxInfo.yearOfAssessment}
            />

            <TableContainer component={Paper}>
                <DepreciationScheduleTable
                    rows={depreciationRows}
                    currency={taxInfo.presentationCurrency}
                    wdvBfPools={wdvBfPools ?? undefined}
                    onWdvBfPoolsChange={wdvBfPoolsChange}
                    wdvBfEditable={wdvBfEditable}
                    baPools={baPools ?? undefined}
                    onBaPoolsChange={onBaPoolsChange}
                    salesProceedPools={salesProceedPools ?? undefined}
                />

                <CommercialBuildScheduleTable
                    currency={taxInfo.presentationCurrency}
                    commercialRows={commercialRowsA}
                    commercialRowsChange={handleCommercialRowsAChange}
                    bf={oneReducingBf}
                    bfChange={oneReducingBfChange}
                    periodFrom={periodFrom}
                    commercialPriorLabel={commercialPriorLabel}
                    commercialPriorValue={commercialPriorValue}
                    onCommercialPriorOverrideChange={onCommercialPriorOverrideChange}
                />

                <CommercialBuildScheduleTable
                    currency={taxInfo.presentationCurrency}
                    commercialRows={commercialRowsB}
                    commercialRowsChange={handleCommercialRowsBChange}
                    nextYear={true}
                    bf={wdvBf}
                    bfChange={wdvBfChange}
                    leasehold={true}
                    periodFrom={periodFrom}
                    commercialPriorLabel={leaseholdPriorLabel}
                    commercialPriorValue={leaseholdPriorValue}
                    onCommercialPriorOverrideChange={onLeaseholdPriorOverrideChange}
                />
            </TableContainer>
        </Box>
    );
}
